import React, {useState, useEffect, useMemo} from 'react';
import {
    Card,
    Table,
    Button,
    Typography,
    Layout,
    Row,
    Col
} from 'antd';
import {PrinterOutlined} from '@ant-design/icons';
import PdfViewer from "../../components/shared/PdfViewer";
import {reportService} from "../../services/domain/report/service";
import {otherService} from "../../services";
import {MemberCheckResponseItem} from "../../model/other/dto/other-api";
import { getPageSizeOptions } from 'utils/pageUtil';

const {Title} = Typography;
const {Content} = Layout;

const MemberLevelCheckPage: React.FC = () => {
    const [data, setData] = useState<MemberCheckResponseItem[] | null>([]);
    const [pdfPreviewVisible, setPdfPreviewVisible] = useState(false);

    useEffect(() => {
        // In a real application, you would fetch data from an API
        // For now, we'll use the mock data
        const getMemberChekList = async () => {
            const data = await otherService.getMemberChekList();
            setData(data);
        }

        getMemberChekList();
    }, []);

    // 關閉PDF預覽視窗
    const handleClosePdfPreview = () => {
        setPdfPreviewVisible(false);
    };

    const handleLoadPdfData = async () => {
        return await reportService.getMembershipLevelCheckPdf();
    }

    /**
     * Export PDF
     */
    const exportPdf = async (filename: string) => {
        // In production, we would handle different report types here
        await reportService.exportMembershipLevelCheckPdf();
    };

    /**
     * Export Excel
     */
    const exportExcel = async (filename: string) => {
        // In production, we would handle different report types here
        await reportService.exportMembershipLevelCheckExcel();
    };


    // Table columns configuration
    const columns = [
        {
            title: '會員證號',
            dataIndex: 'memberId',
            key: 'memberId',
            width: 150,
            align: 'center' as const,
        },
        {
            title: '公司名稱',
            dataIndex: 'companyName',
            key: 'companyName',
            align: 'center' as const,
        },
        {
            title: '會員級數',
            dataIndex: 'membershipLevel',
            key: 'membershipLevel',
            width: 150,
            align: 'center' as const,
        },
        {
            title: '員工數',
            dataIndex: 'employeeCount',
            key: 'employeeCount',
            width: 150,
            align: 'center' as const,
        },
        {
            title: '舊資本額 (元)',
            dataIndex: 'ocapitalAmount',
            key: 'ocapitalAmount',
            width: 150,
            align: 'center' as const,
        },
        {
            title: '新資本額 (元)',
            dataIndex: 'ncapitalAmount',
            key: 'ncapitalAmount',
            width: 150,
            align: 'center' as const,
        },
        {
            title: '動力',
            dataIndex: 'powerUsage',
            key: 'powerUsage',
            width: 150,
            align: 'center' as const,
        },
        {
            title: '修正級數',
            dataIndex: 'adjustedLevel',
            key: 'adjustedLevel',
            width: 150,
            align: 'center' as const,
        },
    ];

    const sanitizedData = useMemo(()=> data ?? [], [data]);

    return (
        <Layout className="member-level-check-page">
            <Content>
                {/* Page Title and Print Button */}
                <Row justify="center" className="mb-lg">
                    <Col>
                        <Title level={3} className="membership-title-with-emoji">
                            會員級數錯誤檢驗
                        </Title>
                    </Col>
                </Row>
                <Row justify="end" className="mb-lg">
                    <Col>
                        <Button
                            type="primary"
                            icon={<PrinterOutlined/>}
                            onClick={() => {
                                setPdfPreviewVisible(true);
                            }}
                        >
                            預覽報表
                        </Button>
                    </Col>
                </Row>

                {/* Main Table */}
                <Card>
                    <Table
                        columns={columns}
                        dataSource={sanitizedData}
                        bordered
                        size="middle"
                        style={{width: '100%'}}
                        rowClassName={() => "editable-row"}
                        rowKey={(record, index) => `${record.memberId}-${index}`}
                        pagination={{
                            defaultPageSize: 50,
                            pageSizeOptions: getPageSizeOptions(sanitizedData.length)
                        }}
                        scroll={{ x: 'max-content', y: 300 }}
                    />
                </Card>

                {/* PDF預覽視窗 */}
                {
                    pdfPreviewVisible && (
                        <PdfViewer
                            visible={pdfPreviewVisible}
                            onClose={handleClosePdfPreview}
                            title="比價證明書預覽"
                            loadPdfData={handleLoadPdfData}
                            exportPdf={exportPdf}
                            exportExcel={exportExcel}
                        />
                    )
                }
            </Content>
        </Layout>
    );
};

export default MemberLevelCheckPage;
